import { useState, useEffect, useRef } from 'react';
import api from 'services/api';

const FIELDS = [
  { key: 'raison_sociale', label: 'Raison sociale' },
  { key: 'forme_juridique', label: 'Forme juridique' },
  { key: 'capital_social', label: 'Capital social' },
  { key: 'secteur', label: 'Secteur' },
  { key: 'responsable', label: 'Responsable' },
  { key: 'titre_responsable', label: 'Titre du responsable' },
  { key: 'rccm', label: 'RCCM' },
  { key: 'nif', label: 'NIF' },
  { key: 'cnss', label: 'CNSS' },
  { key: 'regime_fiscal', label: 'Regime fiscal' },
  { key: 'adresse', label: 'Adresse' },
  { key: 'bp', label: 'Boite postale' },
  { key: 'ville', label: 'Ville' },
  { key: 'pays', label: 'Pays' },
  { key: 'telephone', label: 'Telephone' },
  { key: 'email', label: 'Email' },
  { key: 'site_web', label: 'Site web' },
];

const CODE_TRAVAIL = {
  smig: '52 500 FCFA',
  heures_semaine: '40h',
  heures_sup_1: '+15% (41-48h)',
  heures_sup_2: '+35% (>48h)',
  nuit: '+25% (21h-05h)',
  dimanche_ferie: '+50%',
  conges: '2,5 jours/mois',
  cdd_max: '2 ans initial / 4 ans total cumules — requalification CDI automatique',
  cnss_employeur: '16,5%',
  cnss_salarie: '4%',
};

const ESSAI_CATEGORIES = [
  { categorie: 'CDD (toutes categories)', duree: '1 mois', renouvelable: 'Non' },
  { categorie: 'CDI — Ouvriers / Agents', duree: '1 mois', renouvelable: 'Oui (1 fois)' },
  { categorie: 'CDI — Techniciens / Controleurs / Superviseurs', duree: '3 mois', renouvelable: 'Oui (1 fois)' },
  { categorie: 'CDI — Cadres / DG', duree: '6 mois', renouvelable: 'Non' },
];

const TARIFS = [
  { offre: 'Essentielle', tarif: '130 000 FCFA/agent/mois' },
  { offre: 'Professionnelle', tarif: '160 000 FCFA/agent/mois', badge: 'Populaire' },
  { offre: 'Premium', tarif: '200 000 FCFA/agent/mois', badge: 'Recommande' },
];

const REMISES = [
  { label: 'Mensuel', remise: 'Prix normal' },
  { label: 'Trimestriel', remise: '-5%' },
  { label: 'Annuel', remise: '-15%' },
  { label: 'Fidelite 24 mois', remise: '-10% supplementaire' },
];

const PAYS_AFRIQUE = [
  'Togo','Benin','Ghana','Nigeria','Cote d\'Ivoire','Senegal','Mali','Burkina Faso',
  'Niger','Guinee (Conakry)','Guinee-Bissau','Cameroun','Gabon','Congo','RDC','Tchad','Mauritanie',
  'Algerie','Maroc','Tunisie','Egypte','Afrique du Sud','Kenya','Ethiopie',
  'Tanzanie','Ouganda','Rwanda','Burundi','Mozambique','Angola','Zambie',
  'Zimbabwe','Namibie','Botswana','Madagascar','Maurice','Comores','Djibouti',
  'Somalie','Soudan','Soudan du Sud','Libye','Sierra Leone','Liberia',
  'Cap-Vert','Gambie','Guinee Equatoriale','Sao Tome','Centrafrique',
  'Erythree','Lesotho','Eswatini','Malawi','Seychelles',
].sort();

/* ⚠️ `PRESETS_PAYS` A ETE SUPPRIMEE LE 06/09/2026.
 *
 *    Elle portait 13 pays avec leur organisme et leurs taux de cotisation —
 *    Togo 4/16,5 · Cameroun 4,20/13,55 · Senegal 5,60/14 · etc. — SANS source,
 *    SANS date d'effet, SANS base legale.
 *
 *    Et ce n'etait pas un affichage : le menu « charger un modele pays »
 *    PRE-REMPLISSAIT le formulaire, que l'utilisateur enregistrait ensuite.
 *    Un taux legal INVENTE devenait une donnee tenant par un clic.
 *
 *    ➡️ REGLE DES TROIS NIVEAUX : un taux legal est du NIVEAU 2. Il vit en
 *       base, par pays, avec `date_effet`, `base_legale` et
 *       `source_validation`. Le frontend le LIT via
 *       `GET /charges-sociales/referentiel`. Il ne le porte plus.
 *
 *    ⚠️ Un pays sans referentiel affiche un MESSAGE EXPLICITE. Pas un vide, pas
 *       un zero, pas une valeur par defaut — voir `refLegal` plus bas.
 */

const PAYS_COORDS = {
  'Togo': [6.1375, 1.2123], 'Benin': [6.3654, 2.4183], 'Ghana': [5.6037, -0.1870],
  'Nigeria': [6.5244, 3.3792], 'Cote d\'Ivoire': [5.3600, -4.0083], 'Senegal': [14.6928, -17.4467],
  'Mali': [12.6392, -8.0029], 'Burkina Faso': [12.3714, -1.5197], 'Niger': [13.5137, 2.1098],
  'Guinee': [9.6412, -13.5784], 'Cameroun': [3.8480, 11.5021], 'Gabon': [-0.7893, 11.5094],
  'Congo': [4.2634, 15.2429], 'RDC': [-4.3217, 15.3222], 'Tchad': [12.1348, 15.0557],
  'Mauritanie': [18.0735, -15.9582],
};

export default function ParametresPage() {
  const [tab, setTab] = useState('entreprise');
  const [data, setData] = useState({});
  const [original, setOriginal] = useState({});
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [logoUrl, setLogoUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [carte, setCarte] = useState({ pays: 'Togo', ville: 'Lome', zoom: 7, lat: 6.1375, lng: 1.2123 });
  const [carteSaving, setCarteSaving] = useState(false);
  const [carteTest, setCarteTest] = useState(false);
  const fileRef = useRef(null);

  // Charges sociales state
  const [csConfig, setCsConfig] = useState({
    nom_organisme: '', abreviation: '', pays: 'Togo', site_web: '', numero_employeur: '',
    taux_salarial: 4.00, taux_patronal: 16.50, base_calcul: 'salaire_brut', plafond_mensuel: '',
    mention_bulletin: '', libelle_salarial: 'CNSS', libelle_patronal: 'Charges CNSS employeur',
    mention_pied_bulletin: '', actif: true, mention_suspension: '', date_suspension: '', motif_suspension: '',
  });
  const [csRetenues, setCsRetenues] = useState([]);
  const [csHistorique, setCsHistorique] = useState([]);
  const [csSaving, setCsSaving] = useState(false);
  // Le referentiel legal du pays du tenant. `null` = pas encore charge,
  // `couvert: false` = le pays n'en a pas. Les deux se DISENT differemment.
  const [refLegal, setRefLegal] = useState(null);
  const [, setCsLoaded] = useState(false);

  // ⚠️ LE REFERENTIEL LEGAL SE LIT, IL NE SE DEVINE PAS. Un echec de lecture
  //    n'est PAS « pays non couvert » : on le distingue, sinon une panne reseau
  //    se lirait comme une absence de droit.
  useEffect(() => {
    api.get('/charges-sociales/referentiel')
      .then(({ data }) => setRefLegal(data))
      .catch(() => setRefLegal({ erreur: true,
        message: 'Référentiel légal illisible — panne de lecture, et non absence de référentiel. '
               + 'Ne rien conclure sur les taux de ce pays.' }));
  }, []);

  useEffect(() => {
    api.get('/parametres').then(res => {
      const { logo_url, carte_lat, carte_lng, carte_zoom, carte_ville, pays, ...rest } = res.data;
      setData(rest);
      setOriginal(rest);
      setLogoUrl(logo_url || '');
      if (carte_lat || carte_lng || carte_zoom || carte_ville || pays) {
        setCarte({
          pays: pays || 'Togo',
          ville: carte_ville || 'Lome',
          zoom: carte_zoom || 7,
          lat: carte_lat || 6.1375,
          lng: carte_lng || 1.2123,
        });
      }
    }).catch(() => {});

    // Load charges sociales config
    api.get('/charges-sociales/config').then(r => {
      if (r.data.config) {
        const c = r.data.config;
        setCsConfig({
          nom_organisme: c.nom_organisme || '', abreviation: c.abreviation || '', pays: c.pays || 'Togo',
          site_web: c.site_web || '', numero_employeur: c.numero_employeur || '',
          taux_salarial: Number(c.taux_salarial) || 0, taux_patronal: Number(c.taux_patronal) || 0,
          base_calcul: c.base_calcul || 'salaire_brut', plafond_mensuel: c.plafond_mensuel || '',
          mention_bulletin: c.mention_bulletin || '', libelle_salarial: c.libelle_salarial || 'CNSS',
          libelle_patronal: c.libelle_patronal || '', mention_pied_bulletin: c.mention_pied_bulletin || '',
          actif: c.actif, mention_suspension: c.mention_suspension || '',
          date_suspension: c.date_suspension ? c.date_suspension.split('T')[0] : '',
          motif_suspension: c.motif_suspension || '',
        });
      }
      setCsRetenues(r.data.retenues || []);
      setCsLoaded(true);
    }).catch(() => setCsLoaded(true));

    api.get('/charges-sociales/historique').then(r => setCsHistorique(r.data)).catch(() => {});
  }, []);

  const handleEdit = () => setEditing(true);
  const handleCancel = () => { setData({ ...original }); setEditing(false); };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/parametres', data);
      setOriginal({ ...data });
      setEditing(false);
    } catch { alert('Erreur sauvegarde'); }
    setSaving(false);
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('logo', file);
      const res = await api.post('/parametres/logo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setLogoUrl(res.data.logo_url);
    } catch { alert('Erreur upload logo'); }
    setUploading(false);
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
        {[
          { id: 'entreprise', label: 'Entreprise' },
          { id: 'code_travail', label: 'Code du travail' },
          { id: 'tarifs', label: 'Grille tarifaire' },
          { id: 'systeme', label: 'Systeme' },
          { id: 'charges_sociales', label: 'Charges sociales' },
          { id: 'cartographie', label: 'Cartographie' },
        ].map(t => (
          <button key={t.id} className={`gss-btn ${tab === t.id ? 'gss-btn-primary' : ''}`} onClick={() => setTab(t.id)}>{t.label}</button>
        ))}
      </div>

      {tab === 'entreprise' && (
        <div className="content-card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div className="content-card-title" style={{ marginBottom: 0 }}>Informations de l'entreprise</div>
            {!editing ? (
              <button className="gss-btn gss-btn-primary" onClick={handleEdit} style={{ height: 36, fontSize: 13 }}>Modifier</button>
            ) : (
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="gss-btn" onClick={handleCancel} style={{ height: 36, fontSize: 13 }}>Annuler</button>
                <button className="gss-btn gss-btn-primary" onClick={handleSave} disabled={saving} style={{ height: 36, fontSize: 13, opacity: saving ? 0.6 : 1 }}>
                  {saving ? 'Sauvegarde...' : 'Sauvegarder'}
                </button>
              </div>
            )}
          </div>

          {/* Logo */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20, padding: 16, background: '#F9FAFB', borderRadius: 8 }}>
            <div style={{
              width: 72, height: 72, borderRadius: 8, border: '2px dashed #D1D5DB',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              overflow: 'hidden', background: '#fff',
            }}>
              {logoUrl ? (
                <img src={`${logoUrl}`} alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
              ) : (
                <span style={{ fontSize: 11, color: '#999', textAlign: 'center' }}>Aucun logo</span>
              )}
            </div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Logo de l'entreprise</div>
              <div style={{ fontSize: 11, color: '#888', marginBottom: 8 }}>PNG, JPG ou SVG — max 2 Mo</div>
              <input type="file" ref={fileRef} accept="image/png,image/jpeg,image/svg+xml,image/webp" onChange={handleLogoUpload} style={{ display: 'none' }} />
              <button className="gss-btn" onClick={() => fileRef.current?.click()} disabled={uploading} style={{ height: 30, fontSize: 12, padding: '0 12px' }}>
                {uploading ? 'Upload...' : 'Changer le logo'}
              </button>
            </div>
          </div>

          {/* Fields */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            {FIELDS.map(f => (
              <div key={f.key} style={{ padding: '10px 0', borderBottom: '1px solid #F0F0F0' }}>
                <div style={{ fontSize: '0.72rem', color: 'var(--gss-text-light)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>{f.label}</div>
                {editing ? (
                  <input
                    className="gss-input"
                    value={data[f.key] || ''}
                    onChange={e => setData(d => ({ ...d, [f.key]: e.target.value }))}
                    style={{ width: '100%', fontSize: '0.85rem' }}
                  />
                ) : (
                  <div style={{ fontSize: '0.88rem', fontWeight: 600, color: 'var(--gss-text-primary)' }}>{data[f.key] || '—'}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === 'code_travail' && (
        <div>
          <div className="content-card" style={{ marginBottom: 16 }}>
            <div className="content-card-title">Code du Travail Togolais — Parametres generaux</div>
            <div className="gss-table-wrap">
              <table className="gss-table">
                <thead><tr><th>Parametre</th><th>Valeur</th></tr></thead>
                <tbody>
                  {Object.entries(CODE_TRAVAIL).map(([key, val]) => (
                    <tr key={key}>
                      <td style={{ textTransform: 'capitalize' }}>{key.replace(/_/g, ' ')}</td>
                      <td><strong>{val}</strong></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="content-card">
            <div className="content-card-title">Periode d'essai par categorie</div>
            <div className="gss-table-wrap">
              <table className="gss-table">
                <thead><tr><th>Categorie</th><th>Duree</th><th>Renouvelable</th></tr></thead>
                <tbody>
                  {ESSAI_CATEGORIES.map((e, i) => (
                    <tr key={i}>
                      <td style={{ fontWeight: 600 }}>{e.categorie}</td>
                      <td><strong>{e.duree}</strong></td>
                      <td>{e.renouvelable}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="content-card" style={{ marginTop: 16 }}>
            <div className="content-card-title">Duree maximale CDD</div>
            <div className="gss-table-wrap">
              <table className="gss-table">
                <thead><tr><th>Etape</th><th>Duree</th><th>Regle</th></tr></thead>
                <tbody>
                  <tr>
                    <td style={{ fontWeight: 600 }}>CDD initial</td>
                    <td><strong>Max 2 ans</strong></td>
                    <td>Premiere conclusion du contrat</td>
                  </tr>
                  <tr>
                    <td style={{ fontWeight: 600 }}>CDD renouvele</td>
                    <td><strong>Max 4 ans cumules</strong></td>
                    <td>Renouvellements inclus</td>
                  </tr>
                  <tr>
                    <td style={{ fontWeight: 600 }}>Au-dela 4 ans</td>
                    <td><strong style={{ color: '#DC2626' }}>Requalification CDI</strong></td>
                    <td>Automatique + anciennete conservee</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div style={{ marginTop: 16, padding: 12, background: '#FFF7ED', borderRadius: 8, fontSize: '0.8rem', color: '#9A3412' }}>
              <strong>Article 49 — Code du Travail Togo — Loi n°2021-012.</strong> Tout CDD depassant 4 ans cumules est automatiquement requalifie en CDI par le Tribunal du Travail de Lome.
            </div>
          </div>

          <div style={{ marginTop: 16, padding: 12, background: '#EFF6FF', borderRadius: 8, fontSize: '0.8rem', color: '#1E40AF' }}>
            Ces parametres sont conformes au Code du Travail togolais en vigueur et sont utilises pour le calcul de la paie, des heures supplementaires et des conges.
          </div>
        </div>
      )}

      {tab === 'tarifs' && (
        <div>
          <div className="content-card" style={{ marginBottom: 16 }}>
            <div className="content-card-title">Offres commerciales</div>
            <div className="gss-table-wrap">
              <table className="gss-table">
                <thead><tr><th>Offre</th><th>Tarif</th><th>Badge</th></tr></thead>
                <tbody>
                  {TARIFS.map((t, i) => (
                    <tr key={i}>
                      <td><strong>{t.offre}</strong></td>
                      <td style={{ fontWeight: 600, color: 'var(--gss-red)' }}>{t.tarif}</td>
                      <td>{t.badge ? <span className="gss-badge gss-badge-green">{t.badge}</span> : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="content-card">
            <div className="content-card-title">Remises par engagement</div>
            <div className="gss-table-wrap">
              <table className="gss-table">
                <thead><tr><th>Engagement</th><th>Remise</th></tr></thead>
                <tbody>
                  {REMISES.map((r, i) => (
                    <tr key={i}>
                      <td>{r.label}</td>
                      <td><strong style={{ color: r.remise.includes('-') ? 'var(--gss-success)' : 'var(--gss-text-light)' }}>{r.remise}</strong></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {tab === 'systeme' && (
        <div className="content-card">
          <div className="content-card-title">Configuration systeme</div>
          <div className="gss-table-wrap">
            <table className="gss-table">
              <thead><tr><th>Parametre</th><th>Valeur</th></tr></thead>
              <tbody>
                <tr><td>Version</td><td><strong>GSS-360 v1.0</strong></td></tr>
                <tr><td>Backend</td><td><strong>Node.js / Express — port 3001</strong></td></tr>
                <tr><td>Frontend</td><td><strong>React.js — port 3000</strong></td></tr>
                <tr><td>Base de donnees</td><td><strong>PostgreSQL 17 — gladiator_suite / gladiator_sec</strong></td></tr>
                <tr><td>Authentification</td><td><strong>JWT multi-roles</strong></td></tr>
                <tr><td>Notifications</td><td><strong>Polling 30s — 8 controleurs</strong></td></tr>
                <tr><td>CORS</td><td><strong>localhost:3000, 3003, 3004</strong></td></tr>
                <tr><td>Monnaie</td><td><strong>FCFA (XOF)</strong></td></tr>
                <tr><td>Fuseau horaire</td><td><strong>Africa/Lome (GMT+0)</strong></td></tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'charges_sociales' && (
        <div>
          {/* ══ LE REFERENTIEL LEGAL DU PAYS — SOURCE UNIQUE, NIVEAU 2 ══════════
              ⚠️ Trois etats DISTINCTS, jamais confondus :
                 · en cours de lecture · panne de lecture · pays non couvert.
                 Une panne reseau ne doit pas se lire comme une absence de droit. */}
          {refLegal && refLegal.erreur && (
            <div className="content-card" style={{ marginBottom: 16, borderLeft: '4px solid #B45309' }}>
              <strong style={{ color: '#B45309' }}>⚠️ {refLegal.message}</strong>
            </div>
          )}
          {refLegal && !refLegal.erreur && !refLegal.couvert && (
            <div className="content-card" style={{ marginBottom: 16, borderLeft: '4px solid #B91C1C' }}>
              <strong style={{ color: '#B91C1C' }}>
                Aucun référentiel légal chargé pour {refLegal.pays} — la paie ne peut pas être calculée.
              </strong>
              <div style={{ marginTop: 8, fontSize: 13, color: '#4B5563' }}>{refLegal.message}</div>
            </div>
          )}
          {refLegal && !refLegal.erreur && refLegal.couvert && (
            <div className="content-card" style={{ marginBottom: 16 }}>
              <div className="content-card-title">
                Référentiel légal — {refLegal.pays}
                <span style={{ fontWeight: 400, fontSize: 13, color: '#6B7280', marginLeft: 8 }}>
                  salarial {refLegal.totaux.part_salariale} % · patronal {refLegal.totaux.part_patronale} %
                </span>
              </div>
              <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 8 }}>
                Donnée légale, par pays. Elle se modifie par migration sourcée, jamais depuis cet écran.
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table className="gss-table" style={{ fontSize: 13, minWidth: 760 }}>
                  <thead><tr>
                    <th>Cotisation</th><th>Organisme</th><th>Salarial</th><th>Patronal</th>
                    <th>Effet</th><th>Base légale</th>
                  </tr></thead>
                  <tbody>
                    {refLegal.cotisations.map(c => (
                      <tr key={c.code}>
                        <td>{c.libelle}</td>
                        <td>{c.organisme}</td>
                        <td>{c.part_salariale} %</td>
                        <td>{c.part_patronale} %</td>
                        <td>{String(c.date_effet).slice(0, 10)}</td>
                        <td style={{ fontSize: 12, color: c.base_legale ? '#4B5563' : '#B45309' }}>
                          {c.base_legale || '⚠️ base légale absente'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* ══ PARAMETRES DE PAIE DU PAYS — LUS, JAMAIS CODES ══════════
                  ⚠️ Affiches avec leur UNITE et leur SOURCE. « 30 » sans
                     « jour » et sans « forfait » ne dit pas si un mois de
                     31 jours vaut 30 ou 31. */}
              {(refLegal.parametres || []).length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <div className="content-card-title" style={{ fontSize: 14 }}>
                    Paramètres de paie — {refLegal.pays}
                  </div>
                  <div style={{ overflowX: 'auto' }}>
                    <table className="gss-table" style={{ fontSize: 13, minWidth: 720 }}>
                      <thead><tr>
                        <th>Paramètre</th><th>Valeur</th><th>Effet</th><th>Source</th>
                      </tr></thead>
                      <tbody>
                        {refLegal.parametres.map(p => (
                          <tr key={p.code}>
                            <td>{p.libelle}<div style={{ fontSize: 11, color: '#9CA3AF' }}>{p.code}</div></td>
                            <td><strong>{p.valeur}</strong>{p.unite ? ` ${p.unite}` : ''}</td>
                            <td>{String(p.date_effet).slice(0, 10)}</td>
                            <td style={{ fontSize: 12, color: p.base_legale ? '#4B5563' : '#B45309' }}>
                              {p.base_legale || `⚠️ sans base légale — ${p.source_validation}`}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {refLegal.lignes_sans_base_legale.length > 0 && (
                <div style={{ marginTop: 8, fontSize: 12, color: '#B45309' }}>
                  ⚠️ {refLegal.lignes_sans_base_legale.length} cotisation(s) sans base légale citée :{' '}
                  {refLegal.lignes_sans_base_legale.join(', ')}. La valeur s'applique, sa source reste à écrire.
                </div>
              )}
            </div>
          )}
          {/* Charger modele pays */}
          <div className="content-card" style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div className="content-card-title" style={{ marginBottom: 0 }}>Charges sociales & Protection sociale</div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                {/* ⚠️ PLUS DE « MODELE PAYS » A CHARGER. Le referentiel legal
                    s'AFFICHE, il ne se pre-remplit plus. Un taux qui n'est pas
                    en base n'apparait nulle part. */}
                <span style={{ fontSize: 13, color: '#6B7280' }}>
                  {refLegal === null
                    ? 'Lecture du référentiel légal…'
                    : refLegal.erreur
                      ? '⚠️ Référentiel illisible'
                      : refLegal.couvert
                        ? `Référentiel ${refLegal.pays} : ${refLegal.cotisations.length} cotisation(s)`
                        : `Aucun référentiel légal — ${refLegal.pays}`}
                </span>
                <button
                  className="gss-btn gss-btn-primary"
                  style={{ height: 36, fontSize: 13, opacity: csSaving ? 0.6 : 1 }}
                  disabled={csSaving}
                  onClick={async () => {
                    setCsSaving(true);
                    try {
                      await api.put('/charges-sociales/config', csConfig);
                      const h = await api.get('/charges-sociales/historique');
                      setCsHistorique(h.data);
                      alert('Configuration sauvegardee');
                    } catch { alert('Erreur sauvegarde'); }
                    setCsSaving(false);
                  }}
                >
                  {csSaving ? 'Sauvegarde...' : 'Sauvegarder la configuration'}
                </button>
              </div>
            </div>
            <div style={{ padding: 10, background: '#FFF7ED', borderRadius: 8, fontSize: 12, color: '#9A3412', marginBottom: 16 }}>
              Ces valeurs sont indicatives. Verifiez aupres de votre organisme et modifiez selon votre situation.
            </div>

            {/* Organisme */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#8B1A1A', marginBottom: 10, textTransform: 'uppercase', letterSpacing: 1 }}>Organisme de protection sociale</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 600, color: '#666', display: 'block', marginBottom: 4 }}>Nom complet</label>
                  <input className="gss-input" value={csConfig.nom_organisme} onChange={e => setCsConfig(c => ({ ...c, nom_organisme: e.target.value }))} maxLength={100} style={{ width: '100%', fontSize: 13 }} />
                </div>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 600, color: '#666', display: 'block', marginBottom: 4 }}>Abreviation</label>
                  <input className="gss-input" value={csConfig.abreviation} onChange={e => setCsConfig(c => ({ ...c, abreviation: e.target.value }))} maxLength={20} style={{ width: '100%', fontSize: 13 }} placeholder="Ex: CNSS, CNPS, IPRES" />
                </div>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 600, color: '#666', display: 'block', marginBottom: 4 }}>Pays</label>
                  <select className="gss-input" value={csConfig.pays} onChange={e => setCsConfig(c => ({ ...c, pays: e.target.value }))} style={{ width: '100%', fontSize: 13 }}>
                    {PAYS_AFRIQUE.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 600, color: '#666', display: 'block', marginBottom: 4 }}>Numero employeur</label>
                  <input className="gss-input" value={csConfig.numero_employeur} onChange={e => setCsConfig(c => ({ ...c, numero_employeur: e.target.value }))} maxLength={50} style={{ width: '100%', fontSize: 13 }} placeholder="Ex: 183821" />
                </div>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 600, color: '#666', display: 'block', marginBottom: 4 }}>Site web organisme</label>
                  <input className="gss-input" value={csConfig.site_web} onChange={e => setCsConfig(c => ({ ...c, site_web: e.target.value }))} style={{ width: '100%', fontSize: 13 }} placeholder="Optionnel" />
                </div>
              </div>
            </div>

            {/* Taux */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#8B1A1A', marginBottom: 10, textTransform: 'uppercase', letterSpacing: 1 }}>Taux de cotisation</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 600, color: '#666', display: 'block', marginBottom: 4 }}>
                    Part salariale (%)
                    <span title="Retenue sur salaire brut agent" style={{ marginLeft: 4, cursor: 'help', color: '#999' }}>(?)</span>
                  </label>
                  <input className="gss-input" type="number" step="0.01" min="0" max="99.99" value={csConfig.taux_salarial} onChange={e => setCsConfig(c => ({ ...c, taux_salarial: parseFloat(e.target.value) || 0 }))} style={{ width: '100%', fontSize: 13 }} />
                </div>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 600, color: '#666', display: 'block', marginBottom: 4 }}>
                    Part patronale (%)
                    <span title="A charge de l'employeur" style={{ marginLeft: 4, cursor: 'help', color: '#999' }}>(?)</span>
                  </label>
                  <input className="gss-input" type="number" step="0.01" min="0" max="99.99" value={csConfig.taux_patronal} onChange={e => setCsConfig(c => ({ ...c, taux_patronal: parseFloat(e.target.value) || 0 }))} style={{ width: '100%', fontSize: 13 }} />
                </div>
              </div>
            </div>

            {/* Base de calcul */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#8B1A1A', marginBottom: 10, textTransform: 'uppercase', letterSpacing: 1 }}>Base de calcul</div>
              <div style={{ display: 'flex', gap: 20, marginBottom: 12 }}>
                {[
                  { val: 'salaire_brut', label: 'Salaire brut total' },
                  { val: 'salaire_plafonne', label: 'Salaire brut plafonne' },
                  { val: 'salaire_base', label: 'Salaire de base uniquement' },
                ].map(opt => (
                  <label key={opt.val} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                    <input type="radio" name="base_calcul" checked={csConfig.base_calcul === opt.val} onChange={() => setCsConfig(c => ({ ...c, base_calcul: opt.val }))} />
                    {opt.label}
                  </label>
                ))}
              </div>
              {csConfig.base_calcul === 'salaire_plafonne' && (
                <div style={{ maxWidth: 300 }}>
                  <label style={{ fontSize: 11, fontWeight: 600, color: '#666', display: 'block', marginBottom: 4 }}>Plafond mensuel (FCFA)</label>
                  <input className="gss-input" type="number" value={csConfig.plafond_mensuel} onChange={e => setCsConfig(c => ({ ...c, plafond_mensuel: e.target.value }))} style={{ width: '100%', fontSize: 13 }} placeholder="Ex: 500000" />
                </div>
              )}
            </div>

            {/* Affichage bulletins */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#8B1A1A', marginBottom: 10, textTransform: 'uppercase', letterSpacing: 1 }}>Affichage sur les bulletins</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 600, color: '#666', display: 'block', marginBottom: 4 }}>Libelle ligne salariale</label>
                  <input className="gss-input" value={csConfig.libelle_salarial} onChange={e => setCsConfig(c => ({ ...c, libelle_salarial: e.target.value }))} style={{ width: '100%', fontSize: 13 }} placeholder="Ex: CNSS" />
                </div>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 600, color: '#666', display: 'block', marginBottom: 4 }}>Libelle ligne patronale</label>
                  <input className="gss-input" value={csConfig.libelle_patronal} onChange={e => setCsConfig(c => ({ ...c, libelle_patronal: e.target.value }))} style={{ width: '100%', fontSize: 13 }} placeholder="Ex: Charges CNSS employeur" />
                </div>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 600, color: '#666', display: 'block', marginBottom: 4 }}>Mention sur bulletin</label>
                  <input className="gss-input" value={csConfig.mention_bulletin} onChange={e => setCsConfig(c => ({ ...c, mention_bulletin: e.target.value }))} style={{ width: '100%', fontSize: 13 }} placeholder="Ex: CNSS Togo" />
                </div>
              </div>
              <div style={{ marginTop: 12 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: '#666', display: 'block', marginBottom: 4 }}>Mention pied de bulletin</label>
                <input className="gss-input" value={csConfig.mention_pied_bulletin} onChange={e => setCsConfig(c => ({ ...c, mention_pied_bulletin: e.target.value }))} style={{ width: '100%', fontSize: 13 }} placeholder="Ex: Calcul selon Code du Travail togolais" />
              </div>
            </div>

            {/* Statut */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#8B1A1A', marginBottom: 10, textTransform: 'uppercase', letterSpacing: 1 }}>Statut des declarations</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                  <input type="checkbox" checked={csConfig.actif} onChange={e => setCsConfig(c => ({ ...c, actif: e.target.checked }))} style={{ width: 18, height: 18 }} />
                  <strong>Declarations actives</strong>
                </label>
                <span style={{ fontSize: 12, color: csConfig.actif ? '#16A34A' : '#DC2626', fontWeight: 600 }}>
                  {csConfig.actif ? 'OUI' : 'NON — Taux a 0%'}
                </span>
              </div>
              {!csConfig.actif && (
                <div>
                  <div style={{ padding: 12, background: '#FFF7ED', border: '1px solid #FB923C', borderRadius: 8, marginBottom: 12, fontSize: 12, color: '#9A3412' }}>
                    Les declarations sont suspendues. Les taux sont a 0%. Reactivez quand vous etes pret.
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 600, color: '#666', display: 'block', marginBottom: 4 }}>Mention sur bulletins</label>
                      <input className="gss-input" value={csConfig.mention_suspension} onChange={e => setCsConfig(c => ({ ...c, mention_suspension: e.target.value }))} style={{ width: '100%', fontSize: 13 }} placeholder="Ex: Charges sociales : en cours de regularisation" />
                    </div>
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 600, color: '#666', display: 'block', marginBottom: 4 }}>Date de suspension</label>
                      <input className="gss-input" type="date" value={csConfig.date_suspension} onChange={e => setCsConfig(c => ({ ...c, date_suspension: e.target.value }))} style={{ width: '100%', fontSize: 13 }} />
                    </div>
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 600, color: '#666', display: 'block', marginBottom: 4 }}>Motif</label>
                      <input className="gss-input" value={csConfig.motif_suspension} onChange={e => setCsConfig(c => ({ ...c, motif_suspension: e.target.value }))} style={{ width: '100%', fontSize: 13 }} placeholder="Optionnel" />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Retenues supplementaires */}
          <div className="content-card" style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div className="content-card-title" style={{ marginBottom: 0 }}>Retenues supplementaires</div>
              <button
                className="gss-btn gss-btn-primary"
                style={{ height: 36, fontSize: 13 }}
                onClick={async () => {
                  try {
                    const r = await api.post('/charges-sociales/retenues', { nom: 'Nouvelle retenue', taux: 0, base_calcul: 'salaire_brut' });
                    setCsRetenues(prev => [...prev, r.data]);
                  } catch { alert('Erreur ajout retenue'); }
                }}
              >
                + Ajouter une retenue
              </button>
            </div>
            {csRetenues.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '1.5rem', color: '#999', fontSize: 13 }}>Aucune retenue supplementaire configuree</div>
            ) : (
              <div className="gss-table-wrap">
                <table className="gss-table">
                  <thead><tr><th>Nom</th><th>Taux (%)</th><th>Base</th><th>Actif</th><th style={{ width: 80 }}>Actions</th></tr></thead>
                  <tbody>
                    {csRetenues.map((ret, idx) => (
                      <tr key={ret.id}>
                        <td>
                          <input className="gss-input" value={ret.nom} style={{ fontSize: 13, width: '100%' }} onChange={e => {
                            const v = e.target.value;
                            setCsRetenues(prev => prev.map((r, i) => i === idx ? { ...r, nom: v } : r));
                          }} />
                        </td>
                        <td style={{ width: 100 }}>
                          <input className="gss-input" type="number" step="0.01" value={ret.taux} style={{ fontSize: 13, width: 80 }} onChange={e => {
                            const v = parseFloat(e.target.value) || 0;
                            setCsRetenues(prev => prev.map((r, i) => i === idx ? { ...r, taux: v } : r));
                          }} />
                        </td>
                        <td style={{ width: 160 }}>
                          <select className="gss-input" value={ret.base_calcul} style={{ fontSize: 13 }} onChange={e => {
                            const v = e.target.value;
                            setCsRetenues(prev => prev.map((r, i) => i === idx ? { ...r, base_calcul: v } : r));
                          }}>
                            <option value="salaire_brut">Salaire brut</option>
                            <option value="salaire_base">Salaire de base</option>
                          </select>
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <input type="checkbox" checked={ret.actif} onChange={e => {
                            const v = e.target.checked;
                            setCsRetenues(prev => prev.map((r, i) => i === idx ? { ...r, actif: v } : r));
                          }} />
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: 4 }}>
                            <button className="gss-btn" style={{ height: 30, fontSize: 11, padding: '0 8px' }} onClick={async () => {
                              try { await api.put(`/charges-sociales/retenues/${ret.id}`, ret); } catch { alert('Erreur'); }
                            }}>OK</button>
                            <button className="gss-btn" style={{ height: 30, fontSize: 11, padding: '0 8px', color: '#DC2626' }} onClick={async () => {
                              if (!window.confirm('Supprimer cette retenue ?')) return;
                              try { await api.delete(`/charges-sociales/retenues/${ret.id}`); setCsRetenues(prev => prev.filter(r => r.id !== ret.id)); } catch { alert('Erreur'); }
                            }}>Suppr</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div style={{ marginTop: 12, padding: 10, background: '#F0F9FF', borderRadius: 8, fontSize: 11, color: '#1E40AF' }}>
              Les retenues supplementaires s'ajoutent aux cotisations principales (ex: assurance maladie, retraite complementaire).
            </div>
          </div>

          {/* Historique */}
          {csHistorique.length > 0 && (
            <div className="content-card">
              <div className="content-card-title">Historique des modifications</div>
              <div className="gss-table-wrap">
                <table className="gss-table">
                  <thead><tr><th>Date</th><th>Champ</th><th>Ancienne valeur</th><th>Nouvelle valeur</th><th>Par</th></tr></thead>
                  <tbody>
                    {csHistorique.map(h => (
                      <tr key={h.id}>
                        <td style={{ fontSize: 12 }}>{new Date(h.modifie_le).toLocaleString('fr-FR')}</td>
                        <td style={{ fontWeight: 600 }}>{h.champ_modifie}</td>
                        <td style={{ color: '#DC2626' }}>{h.ancienne_valeur || '—'}</td>
                        <td style={{ color: '#16A34A' }}>{h.nouvelle_valeur || '—'}</td>
                        <td style={{ fontSize: 12 }}>{h.modifie_par_prenom} {h.modifie_par_nom}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {tab === 'cartographie' && (
        <div className="content-card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div className="content-card-title" style={{ marginBottom: 0 }}>Configuration cartographique</div>
            <button
              className="gss-btn gss-btn-primary"
              disabled={carteSaving}
              style={{ height: 36, fontSize: 13, opacity: carteSaving ? 0.6 : 1 }}
              onClick={async () => {
                setCarteSaving(true);
                try {
                  await api.put('/parametres', {
                    ...data,
                    pays: carte.pays,
                    carte_ville: carte.ville,
                    carte_zoom: carte.zoom,
                    carte_lat: carte.lat,
                    carte_lng: carte.lng,
                  });
                  alert('Configuration cartographique sauvegardee');
                } catch { alert('Erreur sauvegarde'); }
                setCarteSaving(false);
              }}
            >
              {carteSaving ? 'Sauvegarde...' : 'Sauvegarder'}
            </button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
            {/* Left column */}
            <div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--gss-text-light)', textTransform: 'uppercase', letterSpacing: 1, display: 'block', marginBottom: 6 }}>Pays principal</label>
                <select
                  className="gss-input"
                  value={carte.pays}
                  onChange={e => {
                    const p = e.target.value;
                    const coords = PAYS_COORDS[p];
                    setCarte(c => ({
                      ...c,
                      pays: p,
                      ...(coords ? { lat: coords[0], lng: coords[1], ville: '' } : {}),
                    }));
                  }}
                  style={{ width: '100%', fontSize: 14 }}
                >
                  {PAYS_AFRIQUE.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>

              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--gss-text-light)', textTransform: 'uppercase', letterSpacing: 1, display: 'block', marginBottom: 6 }}>Ville principale</label>
                <input
                  className="gss-input"
                  value={carte.ville}
                  onChange={e => setCarte(c => ({ ...c, ville: e.target.value }))}
                  placeholder="Ex: Lome, Abidjan..."
                  style={{ width: '100%', fontSize: 14 }}
                />
              </div>

              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--gss-text-light)', textTransform: 'uppercase', letterSpacing: 1, display: 'block', marginBottom: 6 }}>
                  Zoom par defaut: <strong style={{ color: 'var(--gss-red)' }}>{carte.zoom}</strong>
                </label>
                <input
                  type="range"
                  min={5}
                  max={18}
                  value={carte.zoom}
                  onChange={e => setCarte(c => ({ ...c, zoom: parseInt(e.target.value) }))}
                  style={{ width: '100%' }}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#999' }}>
                  <span>5 (continent)</span><span>10 (ville)</span><span>18 (rue)</span>
                </div>
              </div>
            </div>

            {/* Right column */}
            <div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--gss-text-light)', textTransform: 'uppercase', letterSpacing: 1, display: 'block', marginBottom: 6 }}>Latitude</label>
                  <input
                    className="gss-input"
                    type="number"
                    step="0.0001"
                    value={carte.lat}
                    onChange={e => setCarte(c => ({ ...c, lat: parseFloat(e.target.value) || 0 }))}
                    style={{ width: '100%', fontSize: 14 }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--gss-text-light)', textTransform: 'uppercase', letterSpacing: 1, display: 'block', marginBottom: 6 }}>Longitude</label>
                  <input
                    className="gss-input"
                    type="number"
                    step="0.0001"
                    value={carte.lng}
                    onChange={e => setCarte(c => ({ ...c, lng: parseFloat(e.target.value) || 0 }))}
                    style={{ width: '100%', fontSize: 14 }}
                  />
                </div>
              </div>

              <div style={{ padding: 16, background: '#F9FAFB', borderRadius: 8, marginBottom: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>Apercu configuration</div>
                <div style={{ fontSize: 13, lineHeight: 1.8 }}>
                  <div>Pays: <strong>{carte.pays}</strong></div>
                  <div>Centre: <strong>{carte.lat.toFixed(4)}, {carte.lng.toFixed(4)}</strong></div>
                  <div>Zoom: <strong>{carte.zoom}</strong></div>
                  {carte.ville && <div>Ville: <strong>{carte.ville}</strong></div>}
                </div>
              </div>

              <button
                className={`gss-btn ${carteTest ? 'gss-btn-primary' : ''}`}
                style={{ height: 36, fontSize: 13, width: '100%' }}
                onClick={() => {
                  setCarteTest(true);
                  window.open(
                    `https://www.openstreetmap.org/#map=${carte.zoom}/${carte.lat}/${carte.lng}`,
                    '_blank'
                  );
                  setTimeout(() => setCarteTest(false), 1000);
                }}
              >
                Tester la carte
              </button>
            </div>
          </div>

          <div style={{ marginTop: 16, padding: 12, background: '#EFF6FF', borderRadius: 8, fontSize: '0.8rem', color: '#1E40AF' }}>
            Ces parametres definissent le centre et le zoom par defaut de la carte dans le Centre de Commandement. Chaque tenant SaaS peut configurer son propre pays.
          </div>
        </div>
      )}
    </div>
  );
}
